import { useEffect, useMemo, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users, Trash2, RefreshCw, ServerCog, KeyRound, Building2,
  CheckCircle2, PauseCircle, Hourglass, XCircle, Plus,
} from 'lucide-react';
import {
  AdminApiClient,
  type CustomerRow, type CustomerNote, type LicenseRow, type InstallationRow,
} from '../api';
import { useSession } from '../session';
import { usePermissions } from '../lib/permissions';
import { failureFrom, userMessageFor, type Failure } from '../lib/api-state';
import { AccessNotice } from '../components/ui/AccessNotice';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, bandFromStatus } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

type StatusFilter = 'all' | 'active' | 'trial' | 'suspended' | 'expired';

interface Enriched extends CustomerRow {
  license_count: number;
  server_count: number;
}

export function CustomersPage(): JSX.Element {
  const { state } = useSession();
  const [params, setParams] = useSearchParams();
  const focus = params.get('focus');
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [installations, setInstallations] = useState<InstallationRow[]>([]);
  const [err, setErr] = useState<Failure | null>(null);
  // Licenses / installations only feed the per-customer counts. If they
  // fail the counts are unknown ('—'), never silently zero.
  const [countsErr, setCountsErr] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const perms = usePermissions();
  const canMutate = perms.canWrite;
  const canDelete = perms.canOwner;

  // Deep link from the dashboard ("Add customer").
  useEffect(() => {
    if (params.get('new') !== '1') return;
    if (canMutate) setAddOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [params, setParams, canMutate]);

  useEffect(() => {
    setRefreshing(true);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    Promise.allSettled([
      api.customers(200),
      api.licenses({ limit: 500 }),
      api.installations({ limit: 500 }),
    ])
      .then(([cs, ls, is]) => {
        if (cs.status === 'rejected') {
          setRows(null);
          setErr(failureFrom(cs.reason, 'the customer list'));
          return;
        }
        setRows(cs.value.customers);
        setErr(null);
        if (ls.status === 'fulfilled') setLicenses(ls.value.licenses);
        else failureFrom(ls.reason, 'licenses');
        if (is.status === 'fulfilled') setInstallations(is.value.installations);
        else failureFrom(is.reason, 'installations');
        setCountsErr(ls.status === 'rejected' || is.status === 'rejected');
      })
      .finally(() => setRefreshing(false));
  }, [state.token, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const enriched: Enriched[] = useMemo(() => {
    if (!rows) return [];
    const lByCust = new Map<string, number>();
    for (const l of licenses) lByCust.set(l.customer_id, (lByCust.get(l.customer_id) ?? 0) + 1);
    const sByCust = new Map<string, number>();
    for (const i of installations) sByCust.set(i.customer_id, (sByCust.get(i.customer_id) ?? 0) + 1);
    return rows.map((r) => ({
      ...r,
      license_count: lByCust.get(r.id) ?? 0,
      server_count: sByCust.get(r.id) ?? 0,
    }));
  }, [rows, licenses, installations]);

  const totals = useMemo(() => {
    const out = { total: 0, active: 0, trial: 0, suspended: 0, expired: 0 };
    for (const r of rows ?? []) {
      out.total += 1;
      const s = (r.status || '').toLowerCase();
      if (s === 'active') out.active += 1;
      else if (s === 'trial' || s === 'trialing') out.trial += 1;
      else if (s === 'suspended' || s === 'past_due') out.suspended += 1;
      else if (s === 'expired' || s === 'revoked' || s === 'disabled') out.expired += 1;
    }
    return out;
  }, [rows]);

  const filtered: Enriched[] = useMemo(() => {
    if (filter === 'all') return enriched;
    return enriched.filter((r) => {
      const s = (r.status || '').toLowerCase();
      if (filter === 'active')    return s === 'active';
      if (filter === 'trial')     return s === 'trial' || s === 'trialing';
      if (filter === 'suspended') return s === 'suspended' || s === 'past_due';
      if (filter === 'expired')   return s === 'expired' || s === 'revoked' || s === 'disabled';
      return true;
    });
  }, [enriched, filter]);

  const focused: Enriched | null = useMemo(() => {
    if (!focus) return null;
    return enriched.find((r) => r.id === focus) ?? null;
  }, [focus, enriched]);

  const openDrawer = (r: Enriched): void => {
    const next = new URLSearchParams(params);
    next.set('focus', r.id);
    setParams(next, { replace: true });
  };
  const closeDrawer = (): void => {
    const next = new URLSearchParams(params);
    next.delete('focus');
    setParams(next, { replace: true });
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('PERMANENTLY delete this customer? Licenses must be deleted first. This cannot be undone (but the pre-delete row is captured in the audit log).')) return;
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    try {
      await api.deleteCustomer(id);
      closeDrawer();
      setVersion((v) => v + 1);
    } catch (e) {
      alert(`Could not delete the customer. ${userMessageFor(e, 'this customer')}`);
    }
  };

  const columns: Column<Enriched>[] = [
    {
      key: 'company',
      header: 'Customer',
      sortable: true,
      sortValue: (r) => r.company_name,
      cell: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <Avatar name={r.company_name} />
          <PrimaryWithSub primary={r.company_name} sub={r.primary_email} />
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (r) => r.status ?? '',
      cell: (r) => <StatusChip band={bandFromStatus(r.status)}>{r.status || 'unknown'}</StatusChip>,
    },
    {
      key: 'licenses',
      header: 'Licenses',
      align: 'right',
      sortable: !countsErr,
      sortValue: (r) => r.license_count,
      cell: (r) => <CountCell n={countsErr ? null : r.license_count} icon={KeyRound} tone="#22d3ee" />,
    },
    {
      key: 'servers',
      header: 'Servers',
      align: 'right',
      sortable: !countsErr,
      sortValue: (r) => r.server_count,
      cell: (r) => <CountCell n={countsErr ? null : r.server_count} icon={ServerCog} tone="#34d399" />,
    },
    {
      key: 'created',
      header: 'Created',
      align: 'right',
      sortable: true,
      sortValue: (r) => Date.parse(r.created_at),
      cell: (r) => (
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
          {fmtDate(r.created_at)}
        </span>
      ),
    },
  ];

  const filters: Array<{ key: StatusFilter; label: string; count: number; icon: typeof CheckCircle2 }> = [
    { key: 'all',       label: 'All',       count: totals.total,     icon: Users },
    { key: 'active',    label: 'Active',    count: totals.active,    icon: CheckCircle2 },
    { key: 'trial',     label: 'Trial',     count: totals.trial,     icon: Hourglass },
    { key: 'suspended', label: 'Suspended', count: totals.suspended, icon: PauseCircle },
    { key: 'expired',   label: 'Expired',   count: totals.expired,   icon: XCircle },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Customers"
        subtitle="Browse, triage, and manage every customer account."
        icon={Users}
        accent="#c084fc"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            {canMutate && (
              <Button variant="default" size="sm" onClick={() => setAddOpen(true)}>
                <Plus width={14} height={14} />
                Add customer
              </Button>
            )}
          </>
        }
      />

      <AccessNotice reason={canMutate ? null : `${perms.writeReason ?? ''} Adding and deleting customers is not available to you.`} />

      {rows && (
        <HeroPanel
          icon={Building2}
          accent="#c084fc"
          title="Account portfolio"
          subtitle="Where customers stand across plans + lifecycle."
          kpis={[
            <HeroKpi key="total" label="Total" value={totals.total} hint="all accounts" />,
            <HeroKpi key="active" label="Active" value={totals.active} band={totals.active > 0 ? 'healthy' : 'neutral'} />,
            <HeroKpi key="trial" label="On trial" value={totals.trial}
                     band={totals.trial > 0 ? 'degraded' : 'neutral'} />,
            <HeroKpi key="susp" label="Suspended" value={totals.suspended}
                     band={totals.suspended > 0 ? 'critical' : 'neutral'} />,
          ]}
        />
      )}

      {countsErr && rows && (
        <p role="alert" className="err" style={{ margin: '12px 0 0', fontSize: '0.8125rem' }}>
          License and server counts could not be loaded, so they are shown as “—”.{' '}
          <button type="button" onClick={onRefresh}>Retry</button>
        </p>
      )}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows && <FilterChips filters={filters} value={filter} onChange={setFilter} />}

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={openDrawer}
          loading={!rows && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle={filter === 'all' ? 'No customers yet' : `No ${filter} customers`}
          emptyDescription={
            filter === 'all'
              ? 'Customers appear here as accounts are created.'
              : 'Try a different filter to see the rest of the roster.'
          }
        />
      </div>

      <CustomerDrawer
        customer={focused}
        canMutate={canMutate}
        canDelete={canDelete}
        canOwner={perms.canOwner}
        countsUnknown={countsErr}
        canReadNotes={perms.canReadAdminData}
        notesReason={perms.canReadAdminData ? null : 'Customer notes are visible to Owner and Admin accounts.'}
        token={state.token}
        authorId={state.user?.user_id ?? null}
        onClose={closeDrawer}
        onDelete={focused ? () => void onDelete(focused.id) : undefined}
      />

      <AddCustomerDrawer
        open={addOpen}
        token={state.token}
        onClose={() => setAddOpen(false)}
        onCreated={() => { onRefresh(); }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Add customer drawer (POST /internal/customers: company_name +
 * primary_email, owner/admin only). Success is shown only after the
 * server confirms and returns the new id.
 * ────────────────────────────────────────────────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddCustomerDrawer({
  open, token, onClose, onCreated,
}: {
  open: boolean; token: string | null; onClose: () => void; onCreated: () => void;
}): JSX.Element {
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; company: string } | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCompany(''); setEmail(''); setErr(null); setCreated(null); setTouched(false);
  }, [open]);

  const companyErr = !company.trim() ? 'Enter the company name.' : company.trim().length > 200 ? 'Company name must be 200 characters or fewer.' : null;
  const emailErr = !email.trim() ? 'Enter the primary e-mail address.' : !EMAIL_RE.test(email.trim()) ? 'Enter a valid e-mail address, like name@company.com.' : null;

  const submit = async (): Promise<void> => {
    setTouched(true);
    if (companyErr || emailErr) return;
    setBusy(true); setErr(null);
    try {
      const api = new AdminApiClient({ baseUrl: '', getToken: () => token });
      const res = await api.createCustomer({ company_name: company.trim(), primary_email: email.trim() });
      setCreated({ id: res.id, company: company.trim() });
      onCreated();
    } catch (e) {
      setErr(userMessageFor(e, 'the customer'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add customer"
      subtitle="Creates a customer record. Issue a license from the Licenses page afterwards."
      footer={created ? (
        <Button variant="default" size="sm" onClick={onClose}>Done</Button>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="default" size="sm" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Creating…' : 'Create customer'}
          </Button>
        </>
      )}
    >
      {created ? (
        <p role="status" style={{ fontSize: '0.875rem' }}>
          Customer created: <strong>{created.company}</strong>
          <br />
          <code style={{ fontSize: '0.75rem' }}>{created.id}</code>
        </p>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          noValidate
        >
          {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="add-customer-company" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Company name</label>
            <input
              id="add-customer-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              aria-invalid={touched && !!companyErr}
              aria-describedby={touched && companyErr ? 'add-customer-company-err' : undefined}
              maxLength={200}
              autoComplete="organization"
            />
            {touched && companyErr && <span id="add-customer-company-err" role="alert" style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{companyErr}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="add-customer-email" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Primary e-mail</label>
            <input
              id="add-customer-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={touched && !!emailErr}
              aria-describedby={touched && emailErr ? 'add-customer-email-err' : undefined}
              autoComplete="email"
            />
            {touched && emailErr && <span id="add-customer-email-err" role="alert" style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{emailErr}</span>}
          </div>
        </form>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Sub-components
 * ────────────────────────────────────────────────────────────────── */

function Avatar({ name }: { name: string }): JSX.Element {
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    || '?';
  // Deterministic hue from name so the same customer keeps the same color.
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return (
    <div
      aria-hidden
      style={{
        flex: 'none',
        width: 32, height: 32,
        borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.75rem', fontWeight: 600,
        color: `hsl(${hue} 80% 75%)`,
        background: `hsla(${hue} 80% 50% / 0.18)`,
        border: `1px solid hsla(${hue} 80% 50% / 0.35)`,
      }}
    >
      {initials}
    </div>
  );
}

function CountCell({
  n, icon: Icon, tone,
}: { n: number | null; icon: typeof KeyRound; tone: string }): JSX.Element {
  if (n === null) {
    // Count could not be loaded: unknown, not zero.
    return <span title="Not available" aria-label="Not available" style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>;
  }
  if (n === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>0</span>;
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500,
        color: tone,
      }}
    >
      <Icon width={12} height={12} />
      {n.toLocaleString()}
    </span>
  );
}

function FilterChips<T extends string>({
  filters, value, onChange,
}: {
  filters: Array<{ key: T; label: string; count: number; icon: typeof CheckCircle2 }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        padding: 6,
        borderRadius: 12,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
        width: 'fit-content',
        maxWidth: '100%',
      }}
    >
      {filters.map((f) => {
        const active = f.key === value;
        return (
          <button
            key={f.key}
            aria-pressed={active}
            onClick={() => onChange(f.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 8,
              border: 'none',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: active ? '0 1px 0 0 rgba(255,255,255,0.06) inset' : undefined,
            }}
          >
            <f.icon width={12} height={12} />
            {f.label}
            <span
              style={{
                marginLeft: 2,
                fontSize: '0.6875rem',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18,
                textAlign: 'center',
              }}
            >
              {f.count.toLocaleString()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Customer drawer — slide-in detail panel
 * ────────────────────────────────────────────────────────────────── */

function CustomerDrawer({
  customer, canMutate, canDelete, canOwner, countsUnknown, canReadNotes, notesReason, token, authorId, onClose, onDelete,
}: {
  customer: Enriched | null;
  canMutate: boolean;
  canDelete: boolean;
  canOwner: boolean;
  countsUnknown: boolean;
  canReadNotes: boolean;
  notesReason: string | null;
  token: string | null;
  authorId: string | null;
  onClose: () => void;
  onDelete?: () => void;
}): JSX.Element {
  return (
    <Drawer
      open={!!customer}
      onClose={onClose}
      width="lg"
      title={
        customer ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={customer.company_name} />
            <span>{customer.company_name}</span>
          </div>
        ) : ''
      }
      subtitle={customer ? customer.primary_email : undefined}
      footer={
        canDelete && onDelete && (
          <Button variant="destructive" size="sm" onClick={onDelete}>
            <Trash2 width={14} height={14} />
            Delete customer
          </Button>
        )
      }
    >
      {customer && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <DrawerSection title="Overview">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <Field label="Status">
                <StatusChip band={bandFromStatus(customer.status)}>
                  {customer.status || 'unknown'}
                </StatusChip>
              </Field>
              <Field label="Customer ID">
                <code style={{ fontSize: '0.75rem' }}>{customer.id.slice(0, 12)}…</code>
              </Field>
              <Field label="Licenses">
                <CountCell n={countsUnknown ? null : customer.license_count} icon={KeyRound} tone="#22d3ee" />
              </Field>
              <Field label="Servers">
                <CountCell n={countsUnknown ? null : customer.server_count} icon={ServerCog} tone="#34d399" />
              </Field>
              <Field label="Created">
                <span style={{ fontSize: '0.8125rem' }}>{fmtDate(customer.created_at)}</span>
              </Field>
            </div>
          </DrawerSection>

          <DrawerSection title="Notes">
            <NotesPanel
              customerId={customer.id}
              canMutate={canMutate}
              canRead={canReadNotes}
              unavailableReason={notesReason}
              canOwner={canOwner}
              token={token}
              authorId={authorId}
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  );
}

function DrawerSection({
  title, children,
}: { title: string; children: JSX.Element }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: JSX.Element }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Notes panel — save-on-blur editor
 * ────────────────────────────────────────────────────────────────── */

function NotesPanel({
  customerId, canMutate, canRead, unavailableReason, canOwner, token, authorId,
}: {
  customerId: string; canMutate: boolean;
  canRead: boolean; unavailableReason: string | null; canOwner: boolean;
  token: string | null; authorId: string | null;
}): JSX.Element {
  const [notes, setNotes] = useState<CustomerNote[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    // The notes endpoint is owner/admin only on the server (even for
    // reading); other roles get an explanation instead of a failed call.
    if (!canRead) { setNotes(null); return; }
    setNotes(null);
    setErr(null);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => token });
    api.listCustomerNotes(customerId)
      .then((r) => setNotes(r.notes))
      .catch((e) => setErr(failureFrom(e, 'the notes').message));
  }, [customerId, token, version, canRead]);

  const errMsg = (e: unknown): string => userMessageFor(e, 'the note');

  const save = async (): Promise<void> => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true); setErr(null);
    try {
      const api = new AdminApiClient({ baseUrl: '', getToken: () => token });
      await api.createCustomerNote(customerId, body);
      setDraft('');
      setVersion((v) => v + 1);
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };

  const del = async (id: string): Promise<void> => {
    if (!confirm('Delete this note?')) return;
    try {
      const api = new AdminApiClient({ baseUrl: '', getToken: () => token });
      await api.deleteNote(id);
      setVersion((v) => v + 1);
    } catch (e) { alert(errMsg(e)); }
  };

  if (!canRead) {
    return <AccessNotice reason={unavailableReason} />;
  }

  return (
    <div>
      {canMutate && (
        <div style={{ marginBottom: 12 }}>
          <textarea
            aria-label="New note"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void save()}
            rows={2}
            placeholder="Add a note… (saves on blur)"
            disabled={busy}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--bg-overlay)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 4, fontSize: '0.6875rem', color: 'var(--text-muted)',
            }}
          >
            <span>{busy ? 'Saving…' : 'Tab away or click out to save.'}</span>
            {draft.trim() && !busy && (
              <button
                type="button"
                onClick={() => void save()}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--accent-text)', cursor: 'pointer', fontSize: '0.6875rem',
                }}
              >
                Save now
              </button>
            )}
          </div>
        </div>
      )}
      {err && <p className="err" role="alert" style={{ fontSize: '0.75rem' }}>{err}</p>}
      {notes === null && !err && (
        <p role="status" style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Loading…</p>
      )}
      {notes && notes.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
          No notes yet.
        </p>
      )}
      {notes && notes.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.map((n) => (
            <li
              key={n.id}
              style={{
                borderLeft: '3px solid var(--accent)',
                padding: '8px 12px',
                background: 'var(--bg-overlay)',
                borderRadius: 6,
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.8125rem' }}>{n.body}</div>
              <div
                style={{
                  display: 'flex', gap: 8, marginTop: 4,
                  fontSize: '0.6875rem', color: 'var(--text-muted)',
                }}
              >
                <span>{n.author_email ?? 'unknown'}</span>
                <span>·</span>
                <span>{fmtDateTime(n.created_at)}</span>
                {canMutate && (n.author_id === authorId || canOwner) && (
                  <>
                    <span>·</span>
                    <button
                      type="button"
                      onClick={() => void del(n.id)}
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.6875rem',
                      }}
                    >
                      delete
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function fmtDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + 'Z';
}
